/**
 * Dashboard v2 API service — the client half of US-20's two endpoints.
 *
 * US-23 uses `bootstrap` for freshness; the filter options it also returns are
 * what US-05 to US-08 consume, and US-22 will read the same `metadata` block
 * off the composite response.
 */

import type {
  ApiResponse,
  BootstrapResponse,
  CompositeRequest,
  CompositeResponse,
  DashboardFilters,
} from '@delivery-metrics/shared';

import { apiClient } from '../lib/api-client';

const BOOTSTRAP_URL = '/api/v2/dashboard/bootstrap';
const COMPOSITE_URL = '/api/v2/dashboard/top-level';

/**
 * A `Date` as the endpoint's `YYYY-MM-DD`, in **UTC**.
 *
 * `toISOString().slice(0, 10)` and not `toLocaleDateString`: west of
 * Greenwich the local date is a day behind for part of every day, and a range
 * that silently starts a day early is the kind of discrepancy that gets
 * blamed on the rollups.
 */
const utcDay = (value: Date | string): string =>
  new Date(value).toISOString().slice(0, 10);

/**
 * The dashboard's filter state as a composite request body.
 *
 * The v2 vocabulary is deliberately v1's, so this is a rename rather than a
 * translation. `extra: "forbid"` on the server means an unknown field is a
 * 422 rather than a silently ignored filter, so nothing is passed through
 * that the contract does not declare.
 */
export const toCompositeRequest = (
  filters?: DashboardFilters
): CompositeRequest => ({
  startDate: filters?.timeRange?.start ? utcDay(filters.timeRange.start) : null,
  // Sent date-only on purpose. `resolve_effective_range` expands a bare
  // calendar date to 23:59:59.999999 of that UTC day, which is what the v1
  // call site spelled out as `T23:59:59Z`. Same range, one fewer place to get
  // the end-of-day rule wrong.
  endDate: filters?.timeRange?.end ? utcDay(filters.timeRange.end) : null,
  units: filters?.units ?? [],
  programs: filters?.programs ?? [],
  repositories: filters?.repositories ?? [],
  areaPaths: filters?.areaPaths ?? [],
  sonarqubeProjects: filters?.sonarqubeProjects ?? [],
});

/**
 * A bootstrap response together with the validator it was served with.
 *
 * The `ETag` is carried in the caller's cache rather than in module state so
 * that the conditional request has no hidden dependency on call order — a
 * fresh caller gets a fresh request, and a test does not have to reset
 * anything between cases.
 */
export interface BootstrapSnapshot {
  response: BootstrapResponse;
  /** `null` before the first rebuild, when the endpoint emits no validator. */
  etag: string | null;
}

export class DashboardV2ApiService {
  /**
   * Filter options and freshness for one page load.
   *
   * Pass the previous snapshot to revalidate: the endpoint's `ETag` changes
   * only when a rebuild does, so between rebuilds this is a `304` with no
   * body. That matters because US-23 refetches on every window focus.
   */
  static async getBootstrap(
    previous?: BootstrapSnapshot | null
  ): Promise<BootstrapSnapshot> {
    const response = await apiClient.getRaw<ApiResponse<BootstrapResponse>>(
      BOOTSTRAP_URL,
      {
        withCredentials: false,
        headers: previous?.etag
          ? { 'If-None-Match': previous.etag }
          : undefined,
        // A 304 is a successful revalidation, not an error. Without this axios
        // rejects it and the cached response would be thrown away.
        validateStatus: status =>
          status === 304 || (status >= 200 && status < 300),
      }
    );

    if (response.status === 304) {
      if (!previous) {
        // Only reachable if something other than `previous` set the header.
        throw new Error(
          'Received 304 for a bootstrap request with no cached response'
        );
      }
      return previous;
    }

    const payload = response.data?.data;
    if (!payload) {
      throw new Error('Bootstrap response contained no data');
    }

    const etag = response.headers?.etag ?? null;
    return { response: payload, etag: typeof etag === 'string' ? etag : null };
  }

  /**
   * All five KPI blocks from one request (US-22).
   *
   * A POST because the filter body can hold hundreds of area paths, not
   * because it changes anything — see the endpoint's own note.
   */
  static async getComposite(
    filters?: DashboardFilters
  ): Promise<CompositeResponse> {
    const response = await apiClient.post<CompositeResponse>(
      COMPOSITE_URL,
      toCompositeRequest(filters),
      { withCredentials: false }
    );

    if (!response?.data) {
      // Not swallowed into an empty dashboard: five blank KPI cards are
      // indistinguishable from a real period with no activity, and this is
      // the single request the page depends on.
      throw new Error('Composite response contained no data');
    }
    return response.data;
  }
}
